/**
 * Mortals Bundle Section JavaScript
 * Handles pre-built bundles and Build Your Own functionality
 */

class BundleManager {
  constructor(sectionEl = null) {
    this.miniCart = {
      items: [],
      total: 0
    };
    
    this.discountTiers = window.bundleDiscountTiers || [];
    this.settings = window.bundleSettings || {};
    this.currency = this.settings.currency || '${{amount}}';
    // Cache section root to scope all selectors within this section
    this.sectionEl = sectionEl || document.querySelector('.bundles-section');
    this.init();
  }

  // Removed ensureDrawerExists - drawer should be rendered by header

  init() {
    // Tab switching
    this.initTabSwitching();
    
    // Pre-built bundle handlers
    this.initPreBuiltBundles();
    
    // Build Your Own handlers
    this.initBuildYourOwn();
    
    // Mobile mini cart
    this.initMobileMiniCart();
    
    // Load saved bundle from sessionStorage
    this.loadSavedBundle();
  }

  // Legacy single add method matching native product form behavior
  async legacySingleAdd(item) {
    const body = new URLSearchParams();
    body.append('id', item.id);
    body.append('quantity', item.quantity);
    
    // Add bundle properties if they exist
    if (item.properties && item.properties._bundle) {
      body.append('properties[_bundle]', item.properties._bundle);
    }
    
    const response = await fetch('/cart/add.js?opus=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: body.toString()
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add to cart');
    }
    
    return await response.json();
  }

  // Legacy add method for fallback compatibility
  async legacyAdd(items) {
    const results = [];
    const failures = [];
    
    for (const item of items) {
      try {
        const result = await this.legacySingleAdd(item);
        results.push(result);
      } catch (error) {
        console.error(`Failed to add variant ${item.id}:`, error.message);
        failures.push({
          variantId: item.id,
          error: error.message
        });
      }
    }
    
    // Show specific error messages if some items failed
    if (failures.length > 0 && results.length > 0) {
      const failureCount = failures.length;
      const successCount = results.length;
      this.showNotification(
        `Added ${successCount} items. ${failureCount} item(s) unavailable.`,
        'warning'
      );
    } else if (failures.length > 0 && results.length === 0) {
      this.showNotification(
        failures[0].error || 'Unable to add items to cart',
        'error'
      );
    }
    
    return results;
  }

  // Fallback method for sequential cart adds (kept for backwards compatibility)
  async fallbackSequentialAdd(items) {
    // Now just delegates to legacyAdd
    return this.legacyAdd(items);
  }

  // Tab Switching Logic
  initTabSwitching() {
    const root = this.sectionEl || document;
    const tabButtons = root.querySelectorAll('.tab-button');
    const tabContents = root.querySelectorAll('.tab-content');
    
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetTab = button.dataset.tab;
        
        // Update button states
        tabButtons.forEach(btn => {
          const isActive = btn.dataset.tab === targetTab;
          btn.classList.toggle('active', isActive);
          btn.setAttribute('aria-selected', isActive);
          btn.setAttribute('tabindex', isActive ? '0' : '-1');
        });
        
        // Update content visibility and aria-hidden
        tabContents.forEach(content => {
          const isActive = content.dataset.content === targetTab;
          content.classList.toggle('active', isActive);
          content.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        
        // Track event
        this.trackEvent('tab_switch', targetTab);
      });
    });

    // Keyboard navigation for main tabs
    const focusableTabs = Array.from(tabButtons);
    const moveFocus = (currentIndex, delta) => {
      const total = focusableTabs.length;
      const next = (currentIndex + delta + total) % total;
      focusableTabs[next].focus();
      focusableTabs[next].click();
    };
    focusableTabs.forEach((btn, index) => {
      btn.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowRight': e.preventDefault(); moveFocus(index, 1); break;
          case 'ArrowLeft': e.preventDefault(); moveFocus(index, -1); break;
          case 'Home': e.preventDefault(); focusableTabs[0].focus(); focusableTabs[0].click(); break;
          case 'End': e.preventDefault(); focusableTabs[focusableTabs.length - 1].focus(); focusableTabs[focusableTabs.length - 1].click(); break;
        }
      });
    });

    // Sub-tab switching for Build Your Own
    const subTabs = root.querySelectorAll('.sub-tab');
    const productGrids = root.querySelectorAll('.product-grid');
    
    subTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const targetType = tab.dataset.type;
        
        subTabs.forEach(t => {
          const isActive = t.dataset.type === targetType;
          t.classList.toggle('active', isActive);
          t.setAttribute('aria-selected', isActive);
          t.setAttribute('tabindex', isActive ? '0' : '-1');
        });
        productGrids.forEach(grid => {
          const isActive = grid.dataset.productType === targetType;
          grid.classList.toggle('active', isActive);
          grid.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
        
        // Track event
        this.trackEvent('sub_tab_switch', targetType);
      });
    });

    // Keyboard navigation for sub-tabs
    const focusableSubTabs = Array.from(subTabs);
    const moveSubFocus = (currentIndex, delta) => {
      const total = focusableSubTabs.length;
      const next = (currentIndex + delta + total) % total;
      focusableSubTabs[next].focus();
      focusableSubTabs[next].click();
    };
    focusableSubTabs.forEach((btn, index) => {
      btn.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'ArrowRight': e.preventDefault(); moveSubFocus(index, 1); break;
          case 'ArrowLeft': e.preventDefault(); moveSubFocus(index, -1); break;
          case 'Home': e.preventDefault(); focusableSubTabs[0].focus(); focusableSubTabs[0].click(); break;
          case 'End': e.preventDefault(); focusableSubTabs[focusableSubTabs.length - 1].focus(); focusableSubTabs[focusableSubTabs.length - 1].click(); break;
        }
      });
    });

    // Ensure default view is Full Boxes when Build Your Own tab is opened
    const fullTab = root.querySelector('.sub-tab[data-type="full"]');
    const fullGrid = root.querySelector('.product-grid[data-product-type="full"]');
    const sampleTab = root.querySelector('.sub-tab[data-type="sample"]');
    const sampleGrid = root.querySelector('.product-grid[data-product-type="sample"]');
    if (fullTab && fullGrid && sampleTab && sampleGrid) {
      fullTab.classList.add('active');
      fullTab.setAttribute('aria-selected', 'true');
      fullTab.setAttribute('tabindex', '0');
      sampleTab.classList.remove('active');
      sampleTab.setAttribute('aria-selected', 'false');
      sampleTab.setAttribute('tabindex', '-1');
      fullGrid.classList.add('active');
      fullGrid.setAttribute('aria-hidden', 'false');
      sampleGrid.classList.remove('active');
      sampleGrid.setAttribute('aria-hidden', 'true');
    }
  }

  // Pre-Built Bundle Handlers
  initPreBuiltBundles() {
    const root = this.sectionEl || document;
    const bundleButtons = root.querySelectorAll('.add-bundle-btn');
    
    bundleButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        await this.addBundleToCart(button);
      });
    });
  }

  async addBundleToCart(button) {
    const variantIds = button.dataset.variants.split(',').filter(id => id);
    const bundleName = button.dataset.bundleName;
    
    if (variantIds.length === 0) {
      this.showNotification('No products in this bundle', 'error');
      return;
    }
    
    // Update button state
    button.disabled = true;
    button.classList.add('loading');
    
    try {
      // Build items array for bulk add
      const items = variantIds.map(variantId => ({
        id: parseInt(variantId),
        quantity: 1,
        properties: {
          '_bundle': bundleName
        }
      }));

      const formData = { items };

      // Try bulk add first with proper headers
      const response = await fetch('/cart/add.js?opus=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        console.error('Failed to parse response:', e);
        responseData = { description: 'Invalid response from server' };
      }
      
      // Use legacy single-item add if bulk fails
      if (!response.ok) {
        // Use legacy add for compatibility
        const results = await this.legacyAdd(items);
        if (results.length === 0) {
          throw new Error(responseData.description || 'Failed to add to cart');
        }
      }
      
      // Success
      button.classList.remove('loading');
      button.classList.add('success');
      
      // Track event
      this.trackEvent('add_to_cart', 'prebuilt_bundle', bundleName);
      
      // Update cart count
      await this.updateCartCount();

      // Opus handles drawer; no additional pop-up
      
      // Redirect if enabled
      if (this.settings.redirectToCart) {
        window.location.href = '/cart';
      }
      
      // Reset button after delay
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove('success');
      }, 2000);
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      button.classList.remove('loading');
      button.disabled = false;
      // Show exact Shopify error message
      this.showNotification(error.message || 'Error adding bundle to cart', 'error');
    }
  }

  // Build Your Own Handlers
  initBuildYourOwn() {
    // Add to bundle buttons
    const root = this.sectionEl || document;
    const addButtons = root.querySelectorAll('.add-to-bundle-btn');
    addButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.addProductToMiniCart(button);
      });
    });

    // Variant selectors
    const variantSelects = root.querySelectorAll('.bundle-variant-select');
    variantSelects.forEach(select => {
      select.addEventListener('change', (e) => {
        this.updateProductVariant(e.target);
      });
    });

    // Clear bundle button
    const clearButton = root.querySelector('[data-clear-bundle]');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.clearMiniCart();
      });
    }

    // Add bundle to cart button
    const addBundleButton = root.querySelector('[data-add-bundle-to-cart]');
    if (addBundleButton) {
      addBundleButton.addEventListener('click', async (e) => {
        await this.addMiniCartToCart(e.currentTarget);
      });
    }

    // Mobile add bundle button
    const mobileAddButton = root.querySelector('[data-mobile-add-bundle]');
    if (mobileAddButton) {
      mobileAddButton.addEventListener('click', async (e) => {
        await this.addMiniCartToCart(e.currentTarget);
      });
    }
  }

  addProductToMiniCart(button) {
    const product = {
      id: button.dataset.productId,
      title: button.dataset.productTitle,
      variantId: button.dataset.variantId,
      price: parseFloat(button.dataset.price) || 0, // Price is already in cents; fallback to 0
      image: button.dataset.image,
      quantity: 1
    };

    // Check if product already in cart
    const existingIndex = this.miniCart.items.findIndex(item => 
      item.variantId === product.variantId
    );

    if (existingIndex > -1) {
      // Increment quantity if less than 5
      if (this.miniCart.items[existingIndex].quantity < 10) {
        this.miniCart.items[existingIndex].quantity++;
      } else {
        this.showNotification('Maximum quantity reached for this product', 'info');
        return;
      }
    } else {
      // Add new item
      this.miniCart.items.push(product);
    }

    // Update UI
    this.updateMiniCart();
    // If mobile, auto-open the bottom sheet when item is added
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (isMobile) {
      this.openMobileCart();
    }
    
    // Show feedback
    const label = button.querySelector('.button__text') || button;
    label.textContent = 'Added!';
    setTimeout(() => {
      label.textContent = 'Add to Bundle';
    }, 1000);

    // Track event
    this.trackEvent('add_to_bundle', product.title, product.price);
  }

  updateProductVariant(select) {
    const productId = select.dataset.productId;
    const selectedOption = select.options[select.selectedIndex];
    const variantId = selectedOption.value;
    const price = selectedOption.dataset.price;
    const available = selectedOption.dataset.available === 'true';

    // Update button data
    const root = this.sectionEl || document;
    const button = root.querySelector(`.add-to-bundle-btn[data-product-id="${productId}"]`);
    if (!button) return;
    if (button) {
      button.dataset.variantId = variantId;
      button.dataset.price = price;
      button.disabled = !available;
      const label = button.querySelector('.button__text') || button;
      label.textContent = available ? 'Add to Bundle' : 'Sold Out';
    }

    // Update price display
    const priceElement = button && button.closest('.bundle-product-card')
      ? button.closest('.bundle-product-card').querySelector('.bundle-product-card__price')
      : null;
    if (priceElement) {
      priceElement.innerHTML = this.formatMoney(parseFloat(price) || 0);
    }
  }

  updateMiniCart() {
    // Calculate total (prices are stored in cents)
    this.miniCart.total = this.miniCart.items.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);

    // Update UI elements
    this.updateMiniCartUI();
    this.updateProgressBar();
    this.updateMobileCart();

    // Save to session storage
    this.saveBundleToSession();
  }

  updateMiniCartUI() {
    const itemsContainer = document.querySelector('[data-bundle-items]');
    const emptyState = document.querySelector('[data-empty-state]');
    const clearButton = document.querySelector('[data-clear-bundle]');
    const addToCartButton = document.querySelector('[data-add-bundle-to-cart]');

    if (!itemsContainer) return;

    // Clear existing items
    itemsContainer.querySelectorAll('.mini-cart-item').forEach(item => item.remove());

    if (this.miniCart.items.length === 0) {
      // Show empty state
      if (emptyState) emptyState.style.display = 'block';
      if (clearButton) clearButton.style.display = 'none';
      if (addToCartButton) addToCartButton.disabled = true;
    } else {
      // Hide empty state
      if (emptyState) emptyState.style.display = 'none';
      if (clearButton) clearButton.style.display = 'block';
      if (addToCartButton) addToCartButton.disabled = false;

      // Add items
      const template = document.getElementById('bundle-item-template');
      
      this.miniCart.items.forEach((item, index) => {
        const itemElement = template.content.cloneNode(true);
        
        // Populate item data
        itemElement.querySelector('img').src = item.image;
        itemElement.querySelector('img').alt = item.title;
        itemElement.querySelector('.mini-cart-item__title').textContent = item.title;
        itemElement.querySelector('.mini-cart-item__price').textContent = this.formatMoney(item.price);
        
        // Set quantity
        const quantityInput = itemElement.querySelector('.mini-cart-item__quantity-input');
        quantityInput.value = item.quantity;
        quantityInput.dataset.index = index;
        
        // Quantity buttons
        const decreaseBtn = itemElement.querySelector('[data-quantity-decrease]');
        const increaseBtn = itemElement.querySelector('[data-quantity-increase]');
        
        decreaseBtn.dataset.index = index;
        increaseBtn.dataset.index = index;
        
        decreaseBtn.addEventListener('click', () => this.updateQuantity(index, -1));
        increaseBtn.addEventListener('click', () => this.updateQuantity(index, 1));
        
        quantityInput.addEventListener('change', (e) => {
          this.setQuantity(index, parseInt(e.target.value));
        });
        
        // Remove button
        const removeBtn = itemElement.querySelector('[data-remove-item]');
        removeBtn.dataset.index = index;
        removeBtn.addEventListener('click', () => this.removeFromMiniCart(index));
        
        itemsContainer.appendChild(itemElement);
      });
    }

    // Update totals
    this.updateTotals();
  }

  updateProgressBar() {
    const progressFill = document.querySelector('[data-progress-fill]');
    const progressMessage = document.querySelector('[data-progress-message]');
    const discountCallout = document.querySelector('[data-discount-callout]');
    const progressBar = document.querySelector('.discount-progress__bar');
    
    if (!progressFill || !progressMessage) return;

    const total = this.miniCart.total;
    let currentTier = null;
    let nextTier = null;
    // Determine max tier threshold in cents for continuous progress
    const maxTierPriceCents = this.discountTiers.length
      ? Math.max(...this.discountTiers.map(t => t.price * 100))
      : 0;

    // Find current and next tier (tier prices are in dollars, total is in cents)
    this.discountTiers.forEach((tier) => {
      if (total >= tier.price * 100) {
        currentTier = tier;
      } else if (!nextTier) {
        nextTier = tier;
      }
    });

    // Continuous progress from $0 to max tier
    const progress = maxTierPriceCents > 0
      ? Math.min((total / maxTierPriceCents) * 100, 100)
      : 0;

    // Message + tier highlighting
    if (nextTier) {
      const needed = (nextTier.price * 100) - total;
      progressMessage.textContent = `Add ${this.formatMoney(needed)} more for ${nextTier.discount}% off`;
      if (discountCallout) {
        discountCallout.textContent = `You're ${this.formatMoney(needed)} away from ${nextTier.discount}% off`;
      }

      document.querySelectorAll('.discount-tier').forEach(tier => {
        const tierPrice = parseFloat(tier.dataset.tierPrice) * 100; // Convert to cents
        tier.classList.toggle('active', tierPrice === nextTier.price * 100);
        tier.classList.toggle('achieved', tierPrice <= total);
      });
    } else if (currentTier) {
      progressMessage.textContent = `You've unlocked ${currentTier.discount}% off!`;
      if (discountCallout) {
        discountCallout.textContent = `You've unlocked ${currentTier.discount}% off!`;
      }

      document.querySelectorAll('.discount-tier').forEach(tier => {
        const tierPrice = parseFloat(tier.dataset.tierPrice) * 100;
        tier.classList.toggle('achieved', tierPrice <= total);
        tier.classList.remove('active');
      });
    } else {
      progressMessage.textContent = 'Savings Applied at Checkout';
      if (discountCallout) {
        const firstTierPrice = (this.discountTiers && this.discountTiers.length > 0)
          ? this.discountTiers[0].price * 100
          : 0;
        if (firstTierPrice > 0) {
          discountCallout.textContent = `Add ${this.formatMoney(firstTierPrice)} to start saving`;
        } else {
          discountCallout.textContent = '';
        }
      }
      document.querySelectorAll('.discount-tier').forEach(tier => {
        tier.classList.remove('achieved', 'active');
      });
    }

    // Update progress bar (ensure milestones align above, not overlapping)
    progressFill.style.width = `${progress}%`;

    // Render milestone markers based on tiers
    if (progressBar) {
      this.renderProgressMarkers(progressBar);
    }
  }

  updateTotals() {
    const subtotalElement = document.querySelector('[data-bundle-subtotal]');
    const discountElement = document.querySelector('[data-bundle-discount]');
    const totalElement = document.querySelector('[data-bundle-total]');
    const discountRow = document.querySelector('[data-discount-row]');
    const collapsedBarTotal = document.querySelector('[data-mobile-total]');

    if (!subtotalElement) return;

    const subtotal = this.miniCart.total;
    let discountPercent = 0;

    // Find applicable discount (tier prices are in dollars, subtotal is in cents)
    this.discountTiers.forEach(tier => {
      if (subtotal >= tier.price * 100) {
        discountPercent = tier.discount;
      }
    });

    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;

    // Update displays
    subtotalElement.textContent = this.formatMoney(subtotal);
    
    if (discountPercent > 0 && discountRow) {
      discountRow.style.display = 'flex';
      discountElement.textContent = `-${this.formatMoney(discountAmount)}`;
    } else if (discountRow) {
      discountRow.style.display = 'none';
    }
    
    if (totalElement) {
      totalElement.textContent = this.formatMoney(total);
    }

    // Keep collapsed bar price in sync (rounded down whole dollars)
    if (collapsedBarTotal) {
      collapsedBarTotal.textContent = this.formatMoneyWholeDollars(total);
    }
  }

  updateQuantity(index, change) {
    const item = this.miniCart.items[index];
    if (!item) return;

    const newQuantity = item.quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      item.quantity = newQuantity;
      this.updateMiniCart();
    }
  }

  setQuantity(index, quantity) {
    const item = this.miniCart.items[index];
    if (!item) return;

    if (quantity >= 1 && quantity <= 10) {
      item.quantity = quantity;
      this.updateMiniCart();
    }
  }

  removeFromMiniCart(index) {
    this.miniCart.items.splice(index, 1);
    this.updateMiniCart();
    this.trackEvent('remove_from_bundle', 'item_removed');
  }

  clearMiniCart() {
    if (confirm('Are you sure you want to clear your bundle?')) {
      this.miniCart.items = [];
      this.miniCart.total = 0;
      this.updateMiniCart();
      this.trackEvent('clear_bundle', 'bundle_cleared');
    }
  }

  async addMiniCartToCart(button) {
    if (this.miniCart.items.length === 0) return;
    button.disabled = true;
    button.classList.add('loading');

    try {
      // Build items array for bulk add
      const items = this.miniCart.items.map(item => ({
        id: parseInt(item.variantId),
        quantity: item.quantity,
        properties: {
          '_bundle': 'Custom Bundle'
        }
      }));

      const formData = { items };

      // Try bulk add first with proper headers
      const response = await fetch('/cart/add.js?opus=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        console.error('Failed to parse response:', e);
        responseData = { description: 'Invalid response from server' };
      }

      // Use legacy single-item add if bulk fails
      if (!response.ok) {
        // Use legacy add for compatibility
        const results = await this.legacyAdd(items);
        if (results.length === 0) {
          throw new Error(responseData.description || 'Failed to add to cart');
        }
      }

      // Success
      button.classList.remove('loading');
      button.classList.add('success');

      // Track event
      this.trackEvent('add_to_cart', 'custom_bundle', this.miniCart.total);

      // Clear mini cart
      this.miniCart.items = [];
      this.miniCart.total = 0;
      this.updateMiniCart();

      // Update cart count
      await this.updateCartCount();

      // Opus handles drawer; no additional pop-up

      // Close mobile cart if open
      this.closeMobileCart();

      // Redirect if enabled
      if (this.settings.redirectToCart) {
        window.location.href = '/cart';
      }

      // Reset button
      setTimeout(() => {
        button.disabled = false;
        button.classList.remove('success');
      }, 2000);

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      button.classList.remove('loading');
      button.disabled = false;
      // Show exact Shopify error message
      this.showNotification(error.message || 'Error adding bundle to cart', 'error');
    }
  }

  // Mobile Mini Cart
  initMobileMiniCart() {
    const toggleButtons = document.querySelectorAll('[data-toggle-mobile-cart]');
    const closeButton = document.querySelector('[data-close-mobile-cart]');
    const sheet = document.querySelector('[data-mobile-sheet]');
    const backdrop = document.querySelector('[data-mobile-backdrop]');
    const mobileRoot = document.querySelector('.mini-cart-mobile');
    const sheetEl = document.querySelector('[data-mobile-sheet]');
    const dragHandle = document.querySelector('[data-mobile-drag-handle]');
    const collapseBtn = document.querySelector('[data-mobile-collapse]');

    // Ensure the mobile sheet lives at document.body level to avoid clipping
    if (mobileRoot && mobileRoot.parentElement !== document.body) {
      // Copy bundle CSS variables from section scope so visuals remain consistent after moving to <body>
      this.applyBundleCssVariablesToMobileRoot(mobileRoot);
      document.body.appendChild(mobileRoot);
    }

    // Bind all toggles (inline trigger and legacy fixed bar)
    if (toggleButtons.length && sheet) {
      toggleButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.toggleMobileCart());
      });
    }

    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeMobileCart();
      });
    }

    // Close when tapping backdrop or outside
    if (backdrop) {
      backdrop.addEventListener('click', () => this.closeMobileCart());
    }
    if (sheet) {
      sheet.addEventListener('click', (e) => {
        if (e.target === sheet) this.closeMobileCart();
      });
    }

    if (collapseBtn) {
      collapseBtn.addEventListener('click', () => this.closeMobileCart());
    }

    // Swipe-down to close on mobile
    if (sheetEl) {
      let startY = 0;
      let currentY = 0;
      let isDragging = false;

      const onTouchStart = (e) => {
        if (e.touches && e.touches.length) {
          startY = e.touches[0].clientY;
          currentY = startY;
          isDragging = true;
        }
      };
      const onTouchMove = (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        const delta = currentY - startY;
        if (delta > 0) {
          // translate sheet down slightly for feedback
          sheetEl.style.transform = `translateY(${Math.min(delta, 120)}px)`;
        }
      };
      const onTouchEnd = () => {
        if (!isDragging) return;
        const delta = currentY - startY;
        isDragging = false;
        sheetEl.style.transform = '';
        if (delta > 80) {
          this.closeMobileCart();
        }
      };

      sheetEl.addEventListener('touchstart', onTouchStart, { passive: true });
      sheetEl.addEventListener('touchmove', onTouchMove, { passive: true });
      sheetEl.addEventListener('touchend', onTouchEnd, { passive: true });
      if (dragHandle) {
        dragHandle.addEventListener('touchstart', onTouchStart, { passive: true });
        dragHandle.addEventListener('touchmove', onTouchMove, { passive: true });
        dragHandle.addEventListener('touchend', onTouchEnd, { passive: true });
      }
    }

    // ESC key closes the mobile sheet if open (accessibility)
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const sheetOpen = document.querySelector('[data-mobile-sheet]');
        if (sheetOpen && sheetOpen.getAttribute('aria-hidden') === 'false') {
          this.closeMobileCart();
        }
      }
    });
  }

  toggleMobileCart() {
    const sheet = document.querySelector('[data-mobile-sheet]');
    const isOpen = sheet.getAttribute('aria-hidden') === 'false';
    
    if (isOpen) {
      this.closeMobileCart();
    } else {
      this.openMobileCart();
    }
  }

  openMobileCart() {
    const sheet = document.querySelector('[data-mobile-sheet]');
    const backdrop = document.querySelector('[data-mobile-backdrop]');
    const body = document.body;
    const toggleButton = document.querySelector('[data-toggle-mobile-cart]');
    const mobileRoot = document.querySelector('.mini-cart-mobile');

    // Ensure sheet and backdrop are at the end of body for stacking
    if (mobileRoot && mobileRoot.parentElement !== document.body) {
      // Ensure CSS variables are preserved when moving to <body>
      this.applyBundleCssVariablesToMobileRoot(mobileRoot);
      document.body.appendChild(mobileRoot);
    }

    // Open
    sheet.setAttribute('aria-hidden', 'false');
    sheet.classList.add('active');
    // Ensure visible even if external CSS overrides transform
    sheet.style.transform = 'translateY(0)';
    if (backdrop) backdrop.classList.add('active');
    body.classList.add('mobile-cart-open');
    if (toggleButton) toggleButton.setAttribute('aria-expanded', 'true');
    if (mobileRoot) mobileRoot.dataset.open = 'true';

    // Update mobile cart content and lock background scroll
    this.updateMobileCartContent();
    this.lockScrollForSheet();

    // Move focus to close button for accessibility
    const closeButton = document.querySelector('[data-close-mobile-cart]');
    if (closeButton) closeButton.focus();
  }

  closeMobileCart() {
    const sheet = document.querySelector('[data-mobile-sheet]');
    const backdrop = document.querySelector('[data-mobile-backdrop]');
    const body = document.body;
    const mobileRoot = document.querySelector('.mini-cart-mobile');

    sheet.setAttribute('aria-hidden', 'true');
    sheet.classList.remove('active');
    // Restore hidden state explicitly
    sheet.style.transform = 'translateY(100%)';
    if (backdrop) backdrop.classList.remove('active');
    body.classList.remove('mobile-cart-open');
    if (mobileRoot) delete mobileRoot.dataset.open;
    const toggleButton = document.querySelector('[data-toggle-mobile-cart]');
    if (toggleButton) {
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.focus();
    }
    this.unlockScrollForSheet();
  }

  // Ensure mobile cart retains bundle theming when moved outside of the section scope
  applyBundleCssVariablesToMobileRoot(mobileRoot) {
    try {
      const sectionEl = document.querySelector('.bundles-section');
      if (!mobileRoot || !sectionEl) return;
      const computed = getComputedStyle(sectionEl);
      const varsToCopy = [
        '--bundle-primary',
        '--bundle-accent',
        '--bundle-background',
        '--bundle-text',
        '--bundle-border',
        '--bundle-shadow',
        '--bundle-radius',
        '--bundle-transition'
      ];
      varsToCopy.forEach((varName) => {
        const value = computed.getPropertyValue(varName);
        if (value && value.trim() !== '') {
          mobileRoot.style.setProperty(varName, value.trim());
        }
      });
    } catch (e) {
      console.warn('Failed to apply bundle CSS variables to mobile cart root:', e);
    }
  }

  lockScrollForSheet() {
    // Prevent background page from bouncing under the sheet on iOS
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    // Cache current scroll behavior so we can restore it
    const existing = window.getComputedStyle(document.documentElement).scrollBehavior;
    document.documentElement.dataset.prevScrollBehavior = existing || '';
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.top = `-${scrollTop}px`;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.dataset.scrollRestore = String(scrollTop);
  }

  unlockScrollForSheet() {
    const restore = parseInt(document.body.dataset.scrollRestore || '0', 10);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, restore);
    // Restore previous scroll behavior
    const prev = document.documentElement.dataset.prevScrollBehavior || '';
    document.documentElement.style.scrollBehavior = prev;
    delete document.documentElement.dataset.prevScrollBehavior;
    delete document.body.dataset.scrollRestore;
  }

  updateMobileCart() {
    // Update collapsed bar
    const countElement = document.querySelector('[data-mobile-count]');
    const totalElement = document.querySelector('[data-mobile-total]');

    if (countElement) {
      const itemCount = this.miniCart.items.reduce((sum, item) => sum + item.quantity, 0);
      countElement.textContent = `(${itemCount} item${itemCount !== 1 ? 's' : ''})`;
    }

    if (totalElement) {
      const subtotal = this.miniCart.total;
      const discountPercent = this.getApplicableDiscountPercent(subtotal);
      const discountAmount = subtotal * (discountPercent / 100);
      const discountedTotal = subtotal - discountAmount;
      totalElement.textContent = this.formatMoneyWholeDollars(discountedTotal);
    }

    // Update expanded content if open
    const sheet = document.querySelector('[data-mobile-sheet]');
    if (sheet && sheet.getAttribute('aria-hidden') === 'false') {
      this.updateMobileCartContent();
    }
  }

  updateMobileCartContent() {
    // Similar to updateMiniCartUI but for mobile elements
    const itemsContainer = document.querySelector('[data-mobile-bundle-items]');
    const emptyState = document.querySelector('[data-mobile-empty-state]');
    const progressFill = document.querySelector('[data-mobile-progress-fill]');
    const progressMessage = document.querySelector('[data-mobile-progress-message]');
    const subtotalElement = document.querySelector('[data-mobile-subtotal]');
    const discountElement = document.querySelector('[data-mobile-discount]');
    const discountRow = document.querySelector('[data-mobile-discount-row]');
    const totalElement = document.querySelector('[data-mobile-total-summary]');
    const addButton = document.querySelector('[data-mobile-add-bundle]');
    const mobileCallout = document.querySelector('[data-mobile-discount-callout]');

    if (!itemsContainer) return;

    // Clear existing items
    itemsContainer.querySelectorAll('.mini-cart-item').forEach(item => item.remove());

    if (this.miniCart.items.length === 0) {
      if (emptyState) emptyState.style.display = 'block';
      if (addButton) addButton.disabled = true;
    } else {
      if (emptyState) emptyState.style.display = 'none';
      if (addButton) addButton.disabled = false;

      // Add items (similar to desktop)
      const template = document.getElementById('bundle-item-template');
      
      this.miniCart.items.forEach((item, index) => {
        const itemElement = template.content.cloneNode(true);
        
        // Populate item data
        itemElement.querySelector('img').src = item.image;
        itemElement.querySelector('img').alt = item.title;
        itemElement.querySelector('.mini-cart-item__title').textContent = item.title;
        itemElement.querySelector('.mini-cart-item__price').textContent = this.formatMoney(item.price);
        
        // Set quantity
        const quantityInput = itemElement.querySelector('.mini-cart-item__quantity-input');
        quantityInput.value = item.quantity;
        quantityInput.dataset.index = index;
        
        // Event listeners
        const decreaseBtn = itemElement.querySelector('[data-quantity-decrease]');
        const increaseBtn = itemElement.querySelector('[data-quantity-increase]');
        const removeBtn = itemElement.querySelector('[data-remove-item]');
        
        decreaseBtn.addEventListener('click', () => this.updateQuantity(index, -1));
        increaseBtn.addEventListener('click', () => this.updateQuantity(index, 1));
        removeBtn.addEventListener('click', () => this.removeFromMiniCart(index));
        
        itemsContainer.appendChild(itemElement);
      });
    }

    // Update progress and totals (similar to desktop)
    if (progressFill && progressMessage) {
      const progress = this.calculateProgress();
      progressFill.style.width = `${progress.percentage}%`;
      progressMessage.textContent = progress.message;
      if (mobileCallout) {
        // Mirror desktop message but use concise phrasing
        mobileCallout.textContent = progress.message.replace('Add ', "You're ").replace(' more for ', ' away from ');
      }
      // Render milestone markers on mobile progress bar
      const mobileRoot = document.querySelector('.mini-cart-mobile');
      if (mobileRoot) {
        const progressBar = mobileRoot.querySelector('.discount-progress__bar');
        if (progressBar) this.renderProgressMarkers(progressBar);
      }
    }

    // Update totals
    const subtotal = this.miniCart.total;
    let discountPercent = 0;

    this.discountTiers.forEach(tier => {
      if (subtotal >= tier.price * 100) {
        discountPercent = tier.discount;
      }
    });

    const discountAmount = subtotal * (discountPercent / 100);
    const total = subtotal - discountAmount;

    if (subtotalElement) subtotalElement.textContent = this.formatMoney(subtotal);
    
    if (discountPercent > 0 && discountRow) {
      discountRow.style.display = 'flex';
      if (discountElement) discountElement.textContent = `-${this.formatMoney(discountAmount)}`;
    } else if (discountRow) {
      discountRow.style.display = 'none';
    }
    
    if (totalElement) totalElement.textContent = this.formatMoney(total);
  }

  calculateProgress() {
    const total = this.miniCart.total;
    let currentTier = null;
    let nextTier = null;
    let message = 'Savings Applied at Checkout';

    const maxTierPriceCents = this.discountTiers.length
      ? Math.max(...this.discountTiers.map(t => t.price * 100))
      : 0;

    this.discountTiers.forEach((tier) => {
      if (total >= tier.price * 100) {
        currentTier = tier;
      } else if (!nextTier) {
        nextTier = tier;
      }
    });

    const progress = maxTierPriceCents > 0
      ? Math.min((total / maxTierPriceCents) * 100, 100)
      : 0;

    if (nextTier) {
      const needed = (nextTier.price * 100) - total;
      message = `Add ${this.formatMoney(needed)} more for ${nextTier.discount}% off`;
    } else if (currentTier) {
      message = `You've unlocked ${currentTier.discount}% off!`;
    }

    return { percentage: progress, message };
  }
  renderProgressMarkers(progressBar) {
    if (!progressBar) return;
    // Clear any existing markers (leave fill intact)
    progressBar.querySelectorAll('.discount-progress__marker').forEach(el => el.remove());

    const total = this.miniCart.total;
    const maxTierPriceCents = this.discountTiers.length
      ? Math.max(...this.discountTiers.map(t => t.price * 100))
      : 1; // prevent divide by zero

    // Determine next tier to highlight
    let nextTier = null;
    this.discountTiers.forEach((tier) => {
      const threshold = tier.price * 100;
      if (!nextTier && total < threshold) {
        nextTier = tier;
      }
    });

    this.discountTiers.forEach((tier) => {
      const marker = document.createElement('span');
      marker.className = 'discount-progress__marker';
      const position = Math.min((tier.price * 100) / maxTierPriceCents * 100, 100);
      marker.style.left = `${position}%`;

      const threshold = tier.price * 100;
      if (total >= threshold) {
        marker.classList.add('is-achieved');
      } else if (!nextTier || threshold === nextTier.price * 100) {
        marker.classList.add('is-active');
      }
      progressBar.appendChild(marker);
    });
  }

  // Drawer opening handled by Opus Cart. Optional notification shown via settings.
  
  showCartNotification() {
    // Create notification element with cart link
    const notification = document.createElement('div');
    notification.className = 'bundle-notification bundle-notification--success bundle-cart-notification';
    notification.innerHTML = `
      <div class="bundle-notification__content">
        <span>Bundle added to cart!</span>
        <a href="/cart" class="bundle-notification__link">View Cart</a>
      </div>
    `;
    
    // Add accessibility attributes
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');
    
    // Add to body
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => {
      notification.classList.add('active');
    }, 10);
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('active');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 5000);
  }
  // Removed openDrawerDirectly - simplified approach



  // Utility Functions
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.currency.replace('{{amount}}', amount);
  }

  formatMoneyWholeDollars(cents) {
    const whole = Math.floor(cents / 100);
    return this.currency.replace('{{amount}}', String(whole));
  }

  getApplicableDiscountPercent(subtotalCents) {
    let discountPercent = 0;
    this.discountTiers.forEach(tier => {
      if (subtotalCents >= tier.price * 100) {
        discountPercent = tier.discount;
      }
    });
    return discountPercent;
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count in header (theme-specific)
      const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `bundle-notification bundle-notification--${type}`;
    notification.textContent = message;
    
    // Add accessibility attributes
    notification.setAttribute('role', 'status');
    notification.setAttribute('aria-live', 'polite');
    
    // Add to body
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => {
      notification.classList.add('active');
    }, 10);
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('active');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }

  trackEvent(action, label, value = null) {
    // Google Analytics tracking
    if (typeof gtag !== 'undefined') {
      gtag('event', action, {
        'event_category': 'Bundle',
        'event_label': label,
        'value': value
      });
    }
    
    // Shopify Analytics
    if (window.Shopify && window.Shopify.analytics) {
      window.Shopify.analytics.publish('bundle_interaction', {
        action: action,
        label: label,
        value: value
      });
    }
  }

  // Session Storage
  saveBundleToSession() {
    sessionStorage.setItem('bundleCart', JSON.stringify(this.miniCart));
  }

  loadSavedBundle() {
    const saved = sessionStorage.getItem('bundleCart');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data && typeof data === 'object' && Array.isArray(data.items)) {
          this.miniCart = data;
          this.updateMiniCart();
        }
      } catch (error) {
        console.error('Invalid saved bundle data:', error);
        sessionStorage.removeItem('bundleCart');
      }
    }
  }
}

// Initialize when DOM is ready and all scripts are loaded
// This ensures Opus Cart and global.js are already in place
if (document.readyState === 'complete') {
  document.querySelectorAll('.bundles-section').forEach((el) => new BundleManager(el));
} else {
  window.addEventListener('load', () => {
    document.querySelectorAll('.bundles-section').forEach((el) => new BundleManager(el));
  });
}